import { action, computed, makeObservable, observable } from 'mobx'
import { computedFn } from 'mobx-utils'
import { RefObject } from 'react'

import { AvailabilityBlockSource } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { Alert, notNullOrUndefinedFilter } from '@avantstay/backoffice-core'
import { Defined } from '@avantstay/graphql-ts-client'

import { PeriodKind } from '@new/__global/components/TapeChart/TapeChart.types'
import { TapeBlockView } from '@new/domains/bookingsTapeChart/BookingsTapeChart.types'
import { BookingsTapeChartStore } from '@new/domains/bookingsTapeChart/stores/BookingsTapeChartStore'
import { TapeChartBlockStore } from '@new/domains/bookingsTapeChart/stores/TapeChartBlockStore'

import { SidePanelStore, SidePanelType } from '@stores/SidePanelStore'
import { trackError } from '@tracking/customEventReport'

import { BaseStore, TapeType } from '../__types'
import { ParentBlock, UiStateStore } from '../external'
import { scrollController } from '../ScrollController'

export interface BlockAPI {
  setSelected: (selected: boolean) => void
  domRef: RefObject<HTMLDivElement>
}

interface Tape {
  ids: string
  type: TapeType
}

export class TapesStore {
  constructor(
    private getStateStore: () => BaseStore['stateStore'],
    private getUiStateStore: () => Partial<UiStateStore>,
    private getFiltersStore: () => BaseStore['filterStore'],
  ) {
    makeObservable(this, {
      selectionSourceTape: observable,
      tapeRefs: observable,
      selectedFamily: computed,
      selectedBlocks: computed,
      selectParent: action.bound,
      saveTapeApi: action.bound,
      disposeTapeRef: action.bound,
      selectFamily: action.bound,
      select: action.bound,
      deselect: action.bound,
      showTapeSidePanel: action.bound,
    })
  }

  get stateStore() {
    return this.getStateStore()
  }

  get uiStateStore() {
    return this.getUiStateStore()
  }

  get filtersStore() {
    return this.getFiltersStore()
  }

  selectionSourceTape: Tape | undefined = undefined

  tapeRefs: Record<string, BlockAPI> = {}

  get selectedFamily() {
    return this.selectionSourceTape ? this.getTapeFamily(this.selectionSourceTape.ids) : []
  }

  get selectedBlocks(): TapeBlockView[] {
    if (!this.selectionSourceTape) {
      return []
    }

    try {
      return this.selectionSourceTape.ids
        .split(',')
        .map(
          this.selectionSourceTape.type === TapeType.BlockedPeriod
            ? this.getBlockData
            : this.getBookingData,
        )
        .filter(notNullOrUndefinedFilter)
    } catch (e) {
      return []
    }
  }

  getTapeFamily(tapeIds: string) {
    const familySet = new Set([tapeIds])
    return Array.from(
      (this.stateStore.relatedBlocks ?? [])
        ?.filter(({ id, relatedIds }) => tapeIds === id || relatedIds.includes(tapeIds))
        ?.reduce<Set<string>>((family, current) => {
          family.add(current.id)
          current.relatedIds.forEach(id => id && family.add(id))
          return family
        }, familySet),
    )
  }

  private getParentBlock = ({ bookingId, id, ...props }: Defined<ParentBlock>) => {
    const booking = bookingId ? this.getBookingData?.(bookingId) : undefined

    if (booking) {
      return {
        ...booking,
        bookingId,
        period: {
          start: booking.startDate,
          end: booking.endDate,
        },
        note: booking.note || '',
        source: AvailabilityBlockSource.booking,
      }
    }

    const parentBock = this.stateStore.getBlockById?.(id)
    if (parentBock) {
      return parentBock
    }

    // parent tape might not be available in loaded blocks, so we need to push existing id
    return {
      id: bookingId ?? id,
      ...props,
    }
  }

  getBookingData = computedFn((blockId: string): TapeBlockView | undefined => {
    const block = this.stateStore.getBookingById?.(blockId)

    if (!block) {
      return undefined
    }

    return {
      ...block,
      startDate: block.checkInTiming.effectiveTime,
      endDate: block.checkOutTiming.effectiveTime,
      kind: PeriodKind.block,
      reason: block.source, // legacy TC logic
      note: block.notes,
    }
  })

  getBlockData = computedFn((blockId: string): TapeBlockView | undefined => {
    const block = this.stateStore.getBlockById?.(blockId)

    if (!block) {
      return undefined
    }

    const parent = block.parent ? this.getParentBlock(block.parent) : undefined

    return {
      ...block,
      startDate: block.period.start,
      endDate: block.period.end,
      kind: PeriodKind.block,
      reason: block.source, // legacy TC logic
      parent,
      customOpenParentAction: parent ? () => this.selectParent(parent) : undefined,
    }
  })

  selectParent(parent: ReturnType<typeof this.getParentBlock>) {
    if (!parent) {
      return
    }
    this.select({
      ids: parent.id,
      type:
        parent.source === AvailabilityBlockSource.booking
          ? TapeType.Booking
          : TapeType.BlockedPeriod,
    })

    const parentBlock = this.tapeRefs[parent.id]?.domRef.current
    if (parentBlock) {
      // parent block ref could be undefined when it was never in viewport
      // as we render only in-view blocks
      scrollController.scrollIntoView(this.tapeRefs[parent.id]?.domRef.current)
    } else {
      // checking if parent block's home is on the current page
      const parenHomeIndex = this.stateStore.getListingIndex?.(parent.homeId)
      if (parenHomeIndex && parenHomeIndex > -1) {
        scrollController.scrollToRow(parenHomeIndex)
      } else {
        this.uiStateStore.goToPropertyPage?.(parent.homeId)
      }
    }
  }

  saveTapeApi(tapeIds: string, ref: BlockAPI) {
    this.tapeRefs[tapeIds] = ref

    // checking if tape should be selected (for tapes instantioned on scroll)
    return this.selectionSourceTape
      ? this.getTapeFamily(tapeIds).includes(this.selectionSourceTape.ids)
      : false
  }

  disposeTapeRef(tapeId: string) {
    delete this.tapeRefs[tapeId]
  }

  selectFamily(family: string[], select: boolean) {
    family.forEach(tapeId => {
      const setSelected = this.tapeRefs[tapeId]?.setSelected
      if (setSelected) {
        // wrapping with setTimeout to avoid rendering clash:
        // Cannot update a component while rendering a different component
        setTimeout(() => setSelected(select), 0)
      }
    })
  }

  select(tape: Tape) {
    if (this.selectionSourceTape?.ids === tape.ids) {
      return
    }

    if (this.selectionSourceTape) {
      this.deselect(!!tape)
    }

    this.selectionSourceTape = tape
    this.selectFamily(this.getTapeFamily(tape.ids), true)

    // `deselect` leaves the panel open so this can replace it; if nothing opened, the old panel
    // would keep showing a booking that has already been cleared.
    if (!this.showTapeSidePanel()) {
      SidePanelStore.setCurrentSidePanel(undefined)
    }
  }

  deselect(isSelectingNewTape?: boolean) {
    if (!this.selectionSourceTape) {
      return
    }

    const tapeId = this.selectionSourceTape.ids
    const bookingId = this.filtersStore.bookingId
    const blockId = this.filtersStore.blockId

    if (tapeId === bookingId) {
      this.filtersStore.setBookingId?.(undefined)
    }

    if (tapeId === blockId) {
      this.filtersStore.setBlockId?.(undefined)
    }

    this.selectFamily(this.getTapeFamily(this.selectionSourceTape.ids), false)
    this.selectionSourceTape = undefined
    if (!isSelectingNewTape) {
      SidePanelStore.setCurrentSidePanel(undefined)
    }
  }

  // A block tape that resolves to nothing is the failure behind BODI-3411. Reported with the ids
  // and the size of the pool they were looked up in, to show whether `allBlocks` was simply not
  // loaded yet or never contains them.
  protected trackUnresolvedBlockTape(tapeIds: string) {
    Alert.Error('This block could not be loaded. Refresh the page and try again.')

    trackError('Tape chart: block tape could not be resolved', {
      tapeIds,
      requestedIds: tapeIds.split(',').length,
      loadedBlocks: this.stateStore.tapeChartApiStore?.allBlocks?.length,
    })
  }

  // Returns whether a panel was actually opened. A tape whose ids can't be resolved (a block
  // missing from `allBlocks`, say) opens nothing, and the caller has to close whatever was open
  // instead of leaving it showing data for a selection that no longer exists.
  showTapeSidePanel(): boolean {
    if (!this.selectionSourceTape) {
      return false
    }

    if (this.selectionSourceTape.type === TapeType.BlockedPeriod) {
      // check if tape is aggregating block
      if (this.selectedBlocks.length > 1) {
        SidePanelStore.setCurrentSidePanel(SidePanelType.tapeChartViewMultipleBlocks, {
          screenHasSecondaryMenu: true,
        })
        return true
      }

      if (this.selectedBlocks.length === 1) {
        // show block side panel
        BookingsTapeChartStore.setBlockIdParam(this.selectionSourceTape.ids)
        SidePanelStore.setCurrentSidePanel(SidePanelType.tapeChartViewBlock, {
          screenHasSecondaryMenu: true,
        })
        TapeChartBlockStore.setSelectedBlock(this.selectedBlocks[0])
        return true
      }

      this.trackUnresolvedBlockTape(this.selectionSourceTape.ids)
    }

    // check if tape is reservation
    const booking = this.stateStore.getBookingById?.(this.selectionSourceTape.ids)
    if (booking) {
      // show reservation side panel
      BookingsTapeChartStore.setBookingIdParam(booking.id)
      SidePanelStore.setCurrentSidePanel(SidePanelType.tapeChartReservationInfo, {
        screenHasSecondaryMenu: true,
      })
      return true
    }

    return false
  }
}
